import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, PackageIcon, TruckIcon, AlertCircleIcon, CheckCircleIcon, CalendarIcon, TagIcon, MessageSquareIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { VendorUpdate } from '../../data/fulfillmentData';
interface VendorUpdatesFeedProps {
  updates: VendorUpdate[];
}
export const VendorUpdatesFeed: React.FC<VendorUpdatesFeedProps> = ({
  updates
}) => {
  const dk = useDarkClasses();
  if (!updates || updates.length === 0) {
    return <div className={`text-center py-16 rounded-2xl border-2 border-dashed ${dk.border}`} data-id="element-4079" data-mp="28l1cckovmkiv">
        <MessageSquareIcon className={`h-12 w-12 mx-auto mb-4 opacity-40 ${dk.textMuted}`} data-id="element-4080" data-mp="1yoojtq52icpp" />
        <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-4081" data-mp="1umtemak0nf6n">
          No updates yet
        </h3>
        <p className={dk.textSecondary} data-id="element-4082" data-mp="1pngmbaadlfwo">
          This vendor hasn't posted any fulfillment or shipping updates.
        </p>
      </div>;
  }
  const getTypeConfig = (type: VendorUpdate['type']) => {
    switch (type) {
      case 'delay':
        return {
          icon: ClockIcon,
          label: 'Delay',
          colors: dk.isDark ? 'bg-amber-900/30 text-amber-400 border-amber-800/50' : 'bg-amber-100 text-amber-800 border-amber-200',
          dotColor: 'bg-amber-500'
        };
      case 'production-update':
        return {
          icon: PackageIcon,
          label: 'Production Update',
          colors: dk.isDark ? 'bg-blue-900/30 text-blue-400 border-blue-800/50' : 'bg-blue-100 text-blue-800 border-blue-200',
          dotColor: 'bg-blue-500'
        };
      case 'shipping-update':
        return {
          icon: TruckIcon,
          label: 'Shipping Update',
          colors: dk.isDark ? 'bg-indigo-900/30 text-indigo-400 border-indigo-800/50' : 'bg-indigo-100 text-indigo-800 border-indigo-200',
          dotColor: 'bg-indigo-500'
        };
      case 'issue':
        return {
          icon: AlertCircleIcon,
          label: 'Issue',
          colors: dk.isDark ? 'bg-red-900/30 text-red-400 border-red-800/50' : 'bg-red-100 text-red-800 border-red-200',
          dotColor: 'bg-red-500'
        };
      case 'resolved':
        return {
          icon: CheckCircleIcon,
          label: 'Resolved',
          colors: dk.isDark ? 'bg-green-900/30 text-green-400 border-green-800/50' : 'bg-green-100 text-green-800 border-green-200',
          dotColor: 'bg-green-500'
        };
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="max-w-3xl mx-auto" data-id="element-4083" data-mp="wc3zy7u7yi38">
      <div className="mb-8" data-id="element-4084" data-mp="114x5gxxihugd">
        <h2 className={`text-xl font-bold mb-2 ${dk.textHeading}`} data-id="element-4085" data-mp="115q2h76o55lb">
          Order & Product Updates
        </h2>
        <p className={`text-sm ${dk.textSecondary}`} data-id="element-4086" data-mp="155qrflre8z6m">
          Official updates regarding production timelines, shipping delays, and
          resolutions.
        </p>
      </div>

      <motion.div variants={containerVariants} initial="hidden" animate="show" className="relative border-l-2 border-gray-200 dark:border-gray-800 ml-4 md:ml-6 space-y-8 pb-8" data-id="element-4087" data-mp="dg41ce6qkgao">
        {updates.map(update => {
        const config = getTypeConfig(update.type);
        const Icon = config.icon;
        return <motion.div key={update.id} variants={itemVariants} className="relative pl-6 md:pl-8" data-id="element-4088" data-mp="1xuxrip6hxwtz">
              {/* Timeline Dot */}
              <div className={`absolute w-4 h-4 rounded-full -left-[9px] top-1.5 border-4 ${dk.isDark ? 'border-gray-900' : 'border-white'} ${config.dotColor}`} data-id="element-4089" data-mp="1wflgsitvu8g5" />

              <div className={`rounded-xl border p-5 transition-colors hover:shadow-md ${dk.card}`} data-id="element-4090" data-mp="25c3zl28egfz0">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-id="element-4091" data-mp="2fbbkcahgrcw5">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border ${config.colors}`} data-id="element-4092" data-mp="2acqlofhv119t">
                    <Icon className="w-3.5 h-3.5" data-id="element-4093" data-mp="2k2teofozef85" />
                    {config.label}
                  </div>
                  <div className={`text-xs font-medium ${dk.textMuted}`} data-id="element-4094" data-mp="2y2cgkqr78uc0">
                    {formatDate(update.datePosted)}
                  </div>
                </div>

                {/* Message */}
                <p className={`text-sm leading-relaxed mb-4 ${dk.textPrimary}`} data-id="element-4095" data-mp="3cqupgfng85u3">
                  {update.message}
                </p>

                {/* Footer Details */}
                <div className={`flex flex-wrap items-center gap-4 pt-3 border-t ${dk.border}`} data-id="element-4096" data-mp="33po5txpi6ewm">
                  <div className={`flex items-center gap-1.5 text-xs font-medium ${dk.textSecondary}`} data-id="element-4097" data-mp="37pf2gi1pnp92">
                    <TagIcon className="w-3.5 h-3.5" data-id="element-4098" data-mp="2txii506aajn8" />
                    <span className={dk.textMuted} data-id="element-4099" data-mp="2yqto6z39uwe3">Applies to:</span>{' '}
                    {update.affectedScope}
                  </div>

                  {update.estimatedNewTimeline && <div className={`flex items-center gap-1.5 text-xs font-medium ${dk.textSecondary}`} data-id="element-4100" data-mp="2p10ek3mowrch">
                      <CalendarIcon className="w-3.5 h-3.5" data-id="element-4101" data-mp="2fzkeaopla5lz" />
                      <span className={dk.textMuted} data-id="element-4102" data-mp="2fz0x5a7rjl90">New Timeline:</span>{' '}
                      {update.estimatedNewTimeline}
                    </div>}
                </div>
              </div>
            </motion.div>;
      })}
      </motion.div>
    </div>;
};